import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BrandMark } from '../components/BrandMark';
import { Icon } from '../components/Icon';
import {
  DESKTOP_OFFERED,
  canAddExtension,
  chromeExtension,
  desktopPlatforms,
  detectSystem,
} from '../features/downloads/systems';
import { useI18n } from '../i18n/i18n-context';
import { LanguagePicker } from '../i18n/LanguagePicker';
import { useConsent } from '../lib/consent-context';
import { CookieNotice } from './CookieNotice';
import { AppNavigation } from './AppNavigation';
import { AppLink } from './AppLink';
import type { AppView } from './useAppView';

interface AppShellProps {
  readonly view: AppView;
  /** Controls on the right of the header. */
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}

/** The desktop app for this computer, such as "Download for Mac". Phones and tablets have none. */
function DesktopDownload({ view }: { readonly view: AppView }) {
  const { t } = useI18n();
  const [system] = useState(detectSystem);
  const platform = desktopPlatforms.find((candidate) => candidate.system === system);
  if (!platform) return null;
  return (
    <AppLink
      className="site-download"
      to="desktop"
      aria-current={view === 'desktop' ? 'page' : undefined}
    >
      <Icon name="download" />
      {t.navigation.download(platform.name)}
    </AppLink>
  );
}

/** The Chrome extension's store listing, in the desktop app's place while that's held back. */
function ExtensionLink() {
  const { t } = useI18n();
  const [addable] = useState(canAddExtension);
  if (!addable) return null;
  return (
    <a className="site-download" href={chromeExtension.url} target="_blank" rel="noopener">
      <Icon name={chromeExtension.icon} />
      {t.downloads.addToChrome}
    </a>
  );
}

export function AppShell({ view, actions, children }: AppShellProps) {
  const { t } = useI18n();
  const { ask } = useConsent();
  const mainRef = useRef<HTMLElement>(null);
  const previousView = useRef(view);
  useEffect(() => {
    if (previousView.current === view) return;
    previousView.current = view;
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [view]);
  return (
    <div className="app">
      <button
        className="skip-link"
        type="button"
        onClick={() => {
          mainRef.current?.focus();
        }}
      >
        {t.navigation.skipToContent}
      </button>
      <header className="site-header">
        <div className="container site-header-inner">
          <AppLink className="brand" to="home" aria-label="Lyrmo">
            <span className="brand-mark" aria-hidden="true">
              <BrandMark />
            </span>
            <span className="brand-name" aria-hidden="true">
              Lyrmo
            </span>
          </AppLink>
          <AppNavigation view={view} />
          <div className="site-header-actions">
            {DESKTOP_OFFERED ? <DesktopDownload view={view} /> : <ExtensionLink />}
            {actions}
          </div>
        </div>
      </header>
      <main className="container main-content" id="main-content" tabIndex={-1} ref={mainRef}>
        {children}
      </main>
      <footer className="container site-footer">
        <div className="site-footer-inner">
          <p className="footer-tagline">{t.shell.tagline}</p>
          <p className="footer-note">
            {t.shell.aiNote}
            {DESKTOP_OFFERED && (
              <AppLink className="footer-link" to="desktop">
                {t.downloads.link}
              </AppLink>
            )}
            <AppLink className="footer-link" to="guide">
              {t.navigation.guide}
            </AppLink>
            <a className="footer-link" href="/privacy.html">
              {t.shell.privacy}
            </a>
            <button type="button" className="footer-link footer-button" onClick={ask}>
              {t.cookies.settings}
            </button>
            <LanguagePicker />
          </p>
        </div>
      </footer>
      <CookieNotice />
    </div>
  );
}
